import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, MessageSquare, Calendar, AlertTriangle,
  TrendingUp, LogOut, RefreshCw, Search, ChevronDown,
  CheckCircle, Clock, XCircle, Phone, Mail, Building2,
  FileText, Save, Filter, BarChart3, Eye, UserPlus,
  Shield, Trash2, Edit2, UserCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  "Nuevo": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "En proceso": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Contactado": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Ganado": "bg-green-500/20 text-green-400 border-green-500/30",
  "Perdido": "bg-red-500/20 text-red-400 border-red-500/30",
};

const PRIORITY_COLORS = {
  "Alta": "bg-red-500/20 text-red-400",
  "Media": "bg-yellow-500/20 text-yellow-400",
  "Baja": "bg-gray-500/20 text-gray-400",
};

const TYPE_ICONS = {
  "Contacto": <Mail className="w-4 h-4" />,
  "Diagnostico": <FileText className="w-4 h-4" />,
  "Reunion": <Calendar className="w-4 h-4" />,
};

const TYPE_COLORS = {
  "Contacto": "text-blue-400",
  "Diagnostico": "text-purple-400",
  "Reunion": "text-green-400",
};

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:border-white/[0.12] transition-colors" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        {sub && <span className="text-xs text-white/30">{sub}</span>}
      </div>
      <div className="text-2xl font-space font-bold text-white">{value}</div>
      <div className="text-xs text-white/40 mt-1">{label}</div>
    </div>
  );
}

function LeadDetailModal({ lead, onClose, onUpdate, teamMembers }) {
  const [status, setStatus] = useState(lead.status || "Nuevo");
  const [priority, setPriority] = useState(lead.priority || "Media");
  const [notes, setNotes] = useState(lead.admin_notes || "");
  const [assignedTo, setAssignedTo] = useState(lead.assigned_to || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(lead.id, { status, priority, admin_notes: notes, assigned_to: assignedTo });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="lead-detail-modal">
      <div
        className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-space font-bold text-white">{lead.name || "Sin nombre"}</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-sm ${TYPE_COLORS[lead.type]}`}>{lead.type}</span>
                <span className="text-white/30 text-xs">{new Date(lead.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
            </div>
            <button onClick={onClose} className="text-white/40 hover:text-white text-2xl">&times;</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-4">
            {lead.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-white/30" />
                <a href={`mailto:${lead.email}`} className="text-purple-400 hover:underline">{lead.email}</a>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-white/30" />
                <span className="text-white/70">{lead.phone}</span>
              </div>
            )}
            {lead.company && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-white/30" />
                <span className="text-white/70">{lead.company}</span>
              </div>
            )}
            {lead.service && (
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-white/30" />
                <span className="text-white/70">{lead.service}</span>
              </div>
            )}
          </div>

          {/* Meeting info */}
          {lead.type === "Reunion" && (lead.meeting_date || lead.meeting_time) && (
            <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.06]">
              <div className="text-xs text-white/40 uppercase font-space mb-2">Reunion programada</div>
              <div className="text-white/80 text-sm">
                {lead.meeting_date} {lead.meeting_time && `a las ${lead.meeting_time}`}
                {lead.meeting_type && ` via ${lead.meeting_type}`}
              </div>
            </div>
          )}

          {/* Message */}
          {lead.message && (
            <div>
              <div className="text-xs text-white/40 uppercase font-space mb-2">Mensaje</div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.06] text-white/70 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {lead.message}
              </div>
            </div>
          )}

          {/* Status & Priority & Assignment */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-white/40 uppercase font-space mb-2 block">Estado</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-purple-500/50 focus:outline-none appearance-none"
                data-testid="lead-status-select"
              >
                {Object.keys(STATUS_COLORS).map((s) => (
                  <option key={s} value={s} className="bg-[#0a0a0a]">{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase font-space mb-2 block">Prioridad</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-purple-500/50 focus:outline-none appearance-none"
                data-testid="lead-priority-select"
              >
                <option value="Alta" className="bg-[#0a0a0a]">Alta</option>
                <option value="Media" className="bg-[#0a0a0a]">Media</option>
                <option value="Baja" className="bg-[#0a0a0a]">Baja</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase font-space mb-2 block">Asignar a</label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-purple-500/50 focus:outline-none appearance-none"
                data-testid="lead-assign-select"
              >
                <option value="" className="bg-[#0a0a0a]">Sin asignar</option>
                {(teamMembers || []).map((m) => (
                  <option key={m.id} value={m.id} className="bg-[#0a0a0a]">{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Admin notes */}
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Notas del equipo</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:border-purple-500/50 focus:outline-none resize-none"
              placeholder="Agregar notas internas..."
              data-testid="lead-notes-textarea"
            />
          </div>

          {/* Notion link */}
          {lead.notion_page_id && (
            <div className="text-xs text-white/30">
              Notion: <a href={`https://notion.so/${lead.notion_page_id.replace(/-/g, "")}`} target="_blank" rel="noreferrer" className="text-purple-400 hover:underline">Ver en Notion</a>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-space font-semibold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            data-testid="lead-save-button"
          >
            <Save className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState("leads");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const navigate = useNavigate();

  const token = user?.token;
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const perms = user?.role === "admin" 
    ? new Set(["view_leads","edit_leads","manage_users","view_chats","view_stats","assign_leads"])
    : user?.role === "director" 
    ? new Set(["view_leads","edit_leads","view_chats","view_stats","assign_leads"])
    : new Set(["view_leads","view_stats"]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/leads`, { headers, params: { lead_type: filterType || undefined, status: filterStatus || undefined } }),
        axios.get(`${API}/admin/team-members`, { headers }),
      ];
      if (perms.has("view_chats")) {
        promises.push(axios.get(`${API}/admin/chat-sessions`, { headers }));
      }
      if (isAdmin) {
        promises.push(axios.get(`${API}/admin/users`, { headers }));
      }
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setLeads(results[1].data.leads || []);
      setTeamMembers(results[2].data.members || []);
      if (perms.has("view_chats") && results[3]) setChatSessions(results[3].data.sessions || []);
      if (isAdmin && results[perms.has("view_chats") ? 4 : 3]) setTeamUsers(results[perms.has("view_chats") ? 4 : 3].data.users || []);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, filterType, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateLead = async (leadId, updates) => {
    await axios.put(`${API}/admin/leads/${leadId}`, updates, { headers });
    fetchData();
  };

  const handleCreateUser = async (userData) => {
    await axios.post(`${API}/admin/users`, userData, { headers });
    setShowCreateUser(false);
    fetchData();
  };

  const handleUpdateUser = async (userId, updates) => {
    await axios.put(`${API}/admin/users/${userId}`, updates, { headers });
    setEditingUser(null);
    fetchData();
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Seguro que quieres eliminar este usuario?")) return;
    await axios.delete(`${API}/admin/users/${userId}`, { headers });
    fetchData();
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    onLogout();
    navigate("/admin/login");
  };

  const filteredLeads = leads.filter((l) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(term) ||
      (l.email || "").toLowerCase().includes(term) ||
      (l.company || "").toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-space text-lg font-bold text-white">
              disrup<span className="text-purple-400">tiva</span>
              <span className="text-white/30 font-normal ml-2 text-sm">CRM</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchData} className="text-white/40 hover:text-white transition-colors" data-testid="refresh-button">
              <RefreshCw className="w-4 h-4" />
            </button>
            <span className="text-white/30 text-xs font-space">{user?.email}</span>
            <button onClick={handleLogout} className="text-white/40 hover:text-red-400 transition-colors" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
            <StatCard icon={Users} label="Total Leads" value={stats.total_leads} color="text-purple-400" sub={`+${stats.recent_week} esta semana`} />
            <StatCard icon={Mail} label="Contactos" value={stats.contacts} color="text-blue-400" />
            <StatCard icon={FileText} label="Diagnosticos" value={stats.diagnostics} color="text-purple-400" />
            <StatCard icon={Calendar} label="Reuniones" value={stats.schedules} color="text-green-400" />
            <StatCard icon={AlertTriangle} label="Urgentes" value={stats.urgent} color="text-red-400" />
            <StatCard icon={MessageSquare} label="Conversaciones IA" value={stats.unique_sessions} color="text-cyan-400" sub={`${stats.chats} mensajes`} />
            <StatCard icon={TrendingUp} label="Conversion" value={stats.status_breakdown?.Ganado || 0} color="text-green-400" sub="ganados" />
            <StatCard icon={Clock} label="En proceso" value={stats.status_breakdown?.["En proceso"] || 0} color="text-orange-400" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white/[0.03] rounded-lg p-1 w-fit" data-testid="dashboard-tabs">
          {[
            { id: "leads", label: "Leads", icon: Users, show: true },
            { id: "chats", label: "Conversaciones", icon: MessageSquare, show: perms.has("view_chats") },
            { id: "team", label: "Equipo", icon: Shield, show: isAdmin },
          ].filter(t => t.show).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-space transition-colors ${
                activeTab === tab.id ? "bg-purple-600 text-white" : "text-white/40 hover:text-white/60"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6" data-testid="leads-filters">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, email, empresa..."
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-white/20 focus:border-purple-500/50 focus:outline-none"
                  data-testid="search-input"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white/70 text-sm focus:border-purple-500/50 focus:outline-none appearance-none min-w-[140px]"
                data-testid="filter-type"
              >
                <option value="" className="bg-[#0a0a0a]">Todos los tipos</option>
                <option value="contact" className="bg-[#0a0a0a]">Contacto</option>
                <option value="diagnostic" className="bg-[#0a0a0a]">Diagnostico</option>
                <option value="schedule" className="bg-[#0a0a0a]">Reunion</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white/70 text-sm focus:border-purple-500/50 focus:outline-none appearance-none min-w-[140px]"
                data-testid="filter-status"
              >
                <option value="" className="bg-[#0a0a0a]">Todos los estados</option>
                {Object.keys(STATUS_COLORS).map((s) => (
                  <option key={s} value={s} className="bg-[#0a0a0a]">{s}</option>
                ))}
              </select>
            </div>

            {/* Leads Table */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="leads-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Nombre</th>
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Tipo</th>
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Estado</th>
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Prioridad</th>
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Email</th>
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Fecha</th>
                      <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="px-5 py-12 text-center text-white/30 text-sm">Cargando...</td></tr>
                    ) : filteredLeads.length === 0 ? (
                      <tr><td colSpan={7} className="px-5 py-12 text-center text-white/30 text-sm">No se encontraron leads</td></tr>
                    ) : (
                      filteredLeads.map((lead) => (
                        <tr
                          key={lead.id}
                          className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer transition-colors"
                          onClick={() => setSelectedLead(lead)}
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="px-5 py-4">
                            <div className="text-sm text-white font-medium">{lead.name || "Sin nombre"}</div>
                            {lead.company && <div className="text-xs text-white/30">{lead.company}</div>}
                          </td>
                          <td className="px-5 py-4">
                            <span className={`flex items-center gap-1.5 text-xs ${TYPE_COLORS[lead.type]}`}>
                              {TYPE_ICONS[lead.type]}
                              {lead.type}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[lead.status] || STATUS_COLORS["Nuevo"]}`}>
                              {lead.status || "Nuevo"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`text-xs px-2 py-0.5 rounded ${PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS["Media"]}`}>
                              {lead.priority || "Media"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-white/50">{lead.email || "-"}</td>
                          <td className="px-5 py-4 text-xs text-white/30">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short" }) : "-"}
                          </td>
                          <td className="px-5 py-4">
                            <Eye className="w-4 h-4 text-white/20 hover:text-purple-400 transition-colors" />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {!loading && (
                <div className="px-5 py-3 border-t border-white/[0.06] text-xs text-white/30">
                  {filteredLeads.length} leads encontrados
                </div>
              )}
            </div>
          </>
        )}

        {/* Chats Tab */}
        {activeTab === "chats" && (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="chats-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Sesion</th>
                  <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Primer mensaje</th>
                  <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Mensajes</th>
                  <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Ultima actividad</th>
                </tr>
              </thead>
              <tbody>
                {chatSessions.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-12 text-center text-white/30 text-sm">No hay conversaciones</td></tr>
                ) : (
                  chatSessions.map((s) => (
                    <tr key={s.session_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`chat-row-${s.session_id}`}>
                      <td className="px-5 py-4 text-xs text-white/50 font-mono">{s.session_id.slice(0, 8)}...</td>
                      <td className="px-5 py-4 text-sm text-white/70 max-w-xs truncate">{s.first_message || "-"}</td>
                      <td className="px-5 py-4">
                        <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 rounded">{s.messages}</span>
                      </td>
                      <td className="px-5 py-4 text-xs text-white/30">{s.last_timestamp ? new Date(s.last_timestamp).toLocaleDateString("es-ES") : "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === "team" && isAdmin && (
          <div data-testid="team-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-space font-bold text-white">Equipo</h2>
              <button
                onClick={() => setShowCreateUser(true)}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-space px-4 py-2 rounded-lg transition-colors"
                data-testid="create-user-button"
              >
                <UserPlus className="w-4 h-4" />
                Nuevo usuario
              </button>
            </div>

            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Nombre</th>
                    <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Email</th>
                    <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Rol</th>
                    <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Estado</th>
                    <th className="text-left px-5 py-3 text-xs text-white/40 font-space uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {teamUsers.map((u) => (
                    <tr key={u.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-4 text-sm text-white font-medium">{u.name}</td>
                      <td className="px-5 py-4 text-sm text-white/50">{u.email}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${
                          u.role === "admin" ? "bg-purple-500/20 text-purple-400 border-purple-500/30" :
                          u.role === "director" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                          "bg-gray-500/20 text-gray-400 border-gray-500/30"
                        }`}>
                          {u.role === "admin" ? "Admin" : u.role === "director" ? "Director" : "Colaborador"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-0.5 rounded ${u.active !== false ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                          {u.active !== false ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingUser(u)}
                            className="text-white/30 hover:text-purple-400 transition-colors"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {u.id !== user.id && (
                            <button
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-white/30 hover:text-red-400 transition-colors"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Lead detail modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
          teamMembers={teamMembers}
        />
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <CreateUserModal onClose={() => setShowCreateUser(false)} onCreate={handleCreateUser} />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onUpdate={handleUpdateUser} />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onCreate }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("colaborador");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onCreate({ email, name, password, role });
    } catch (err) {
      setError(err.response?.data?.detail || "Error al crear usuario");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="create-user-modal">
      <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-space font-bold text-white">Nuevo usuario</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm">{error}</div>
          )}
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Nombre</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/20 focus:border-purple-500/50 focus:outline-none"
              placeholder="Nombre completo" data-testid="new-user-name" />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/20 focus:border-purple-500/50 focus:outline-none"
              placeholder="usuario@disruptiva.online" data-testid="new-user-email" />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Contrasena</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/20 focus:border-purple-500/50 focus:outline-none"
              placeholder="Min. 6 caracteres" data-testid="new-user-password" />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Rol</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-purple-500/50 focus:outline-none appearance-none"
              data-testid="new-user-role">
              <option value="colaborador" className="bg-[#0a0a0a]">Colaborador</option>
              <option value="director" className="bg-[#0a0a0a]">Director</option>
              <option value="admin" className="bg-[#0a0a0a]">Admin</option>
            </select>
            <p className="text-xs text-white/25 mt-2">
              {role === "admin" ? "Control total: leads, usuarios, chats, asignaciones" :
               role === "director" ? "Ve y edita leads, chats, puede asignar leads al equipo" :
               "Solo ve leads asignados y estadisticas generales"}
            </p>
          </div>
          <button type="submit" disabled={saving}
            className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-space font-semibold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            data-testid="submit-create-user">
            <UserPlus className="w-4 h-4" />
            {saving ? "Creando..." : "Crear usuario"}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user: editUser, onClose, onUpdate }) {
  const [name, setName] = useState(editUser.name || "");
  const [role, setRole] = useState(editUser.role || "colaborador");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(editUser.active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const updates = { name, role, active };
      if (password) updates.password = password;
      await onUpdate(editUser.id, updates);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al actualizar");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-user-modal">
      <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-space font-bold text-white">Editar: {editUser.name}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm">{error}</div>
          )}
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Nombre</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white text-sm focus:border-purple-500/50 focus:outline-none"
              data-testid="edit-user-name" />
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Rol</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-purple-500/50 focus:outline-none appearance-none"
              data-testid="edit-user-role">
              <option value="colaborador" className="bg-[#0a0a0a]">Colaborador</option>
              <option value="director" className="bg-[#0a0a0a]">Director</option>
              <option value="admin" className="bg-[#0a0a0a]">Admin</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-white/40 uppercase font-space mb-2 block">Nueva contrasena (dejar vacio para no cambiar)</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/20 focus:border-purple-500/50 focus:outline-none"
              placeholder="Dejar vacio para mantener" data-testid="edit-user-password" />
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setActive(!active)}
              className={`w-10 h-6 rounded-full transition-colors ${active ? "bg-green-500" : "bg-white/10"}`}
              data-testid="edit-user-active-toggle">
              <div className={`w-4 h-4 rounded-full bg-white transition-transform mx-1 ${active ? "translate-x-4" : ""}`} />
            </button>
            <span className="text-sm text-white/60">{active ? "Cuenta activa" : "Cuenta desactivada"}</span>
          </div>
          <button type="submit" disabled={saving}
            className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-space font-semibold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            data-testid="submit-edit-user">
            <Save className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </form>
      </div>
    </div>
  );
}
